import React, { useState, useEffect } from 'react';

const pillars = [
  {
    num: '01',
    collapsed: 'VENTURE CAPITAL',
    category: 'Venture Capital',
    title: 'Deep-Tech Moats & Architectural Defensibility',
    desc: 'Underwrite algorithmic defensibility and true technical architecture before term sheets. Frontier stress-tests software codebases, audits proprietary data moats, models synthetic unit economics, and aligns portfolio roadmaps for proactive strategic M&A exits.',
    image: 'https://images.unsplash.com/photo-1506146332389-18140dc7b2fb?q=80&w=1600&auto=format&fit=crop',
    link: 'Explore VC operational lens',
    tag: '48h Moat Audits',
  },
  {
    num: '02',
    collapsed: 'PRIVATE EQUITY',
    category: 'Private Equity',
    title: 'High-Conviction Thesis Underwriting',
    desc: 'Underwrite non-consensus investment theses that survive beyond the closing dinner. Frontier eliminates conviction lag in thematic sourcing, stress-tests commercial moats and returns with adversarial IC rigor, and bridges diligence directly into value creation and strategic transformation.',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop',
    link: 'Explore PE operational lens',
    tag: 'Deterministic LBO Math',
  },
  {
    num: '03',
    collapsed: 'CORPORATE M&A',
    category: 'Corporate Strategy & M&A',
    title: 'Always-On Strategic Intelligence',
    desc: 'Replace episodic consulting studies with an in-tenant, continuous deal horsepower desk. Frontier autonomously monitors global competitive disruption, evaluates multi-scenario build vs. buy and JV alternatives, and formulates immediate post-close integration playbooks.',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop',
    link: 'Explore Corporate lens',
    tag: 'Continuous M&A Engine',
  },
  {
    num: '04',
    collapsed: 'STRATEGY ADVISORY',
    category: 'Strategy & M&A Advisory',
    title: 'Decoupling Scale from Headcount',
    desc: 'Break the linear labor pyramid by turning 48-hour diligence into an unfair advisory advantage. Deliver audit-grade commercial due diligence decks and technical reviews that win competitive mandates and expand practice margins without analyst burnout.',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1600&auto=format&fit=crop',
    link: 'Explore Advisory lens',
    tag: '10x CDD Velocity',
  },
  {
    num: '05',
    collapsed: 'INVESTMENT BANKING',
    category: 'Investment Banking',
    title: 'Elevating Bankers to Strategic Consiglieres',
    desc: 'Free senior dealmakers from bullpen oversight to focus strictly on board-level advisory. Frontier automates contextual CIM generation, comp spreads, and strategic buyer universe mapping with cell-to-source provenance and zero data leakage.',
    image: 'https://images.unsplash.com/photo-1519501025264-65ba15a82390?q=80&w=1600&auto=format&fit=crop',
    link: 'Explore IB operational lens',
    tag: 'Zero-Leakage CIMs',
  },
];

export const FundPracticeSection = ({ onOpenBriefing }) => {
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => {
    const handler = (e) => {
      if (typeof e.detail === 'number') setActiveIdx(e.detail);
    };
    window.addEventListener('frontier:activate-pillar', handler);
    return () => window.removeEventListener('frontier:activate-pillar', handler);
  }, []);

  return (
    <section id="practices" className="scroll-mt-16 py-24 bg-[#FBFBFB] border-b border-slate-200">
      <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-8 lg:px-12">
        {/* Header Lockup */}
        <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <span className="text-xs font-mono uppercase tracking-widest text-[#F58220] font-semibold block mb-3">PRACTICE LENSES</span>
          <h2 className="text-3xl sm:text-5xl font-extrabold text-[#0F172A] tracking-tight mb-4">
            Every Fund. Every Mandate.
          </h2>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            Different balance sheets carry different priorities. Frontier is purpose-built to execute the exact underwriting stakes and strategic mandates of your firm.
          </p>
        </div>

        {/* Monolith Pillars Container */}
        <div className="hidden md:flex gap-4 h-[600px] transition-all duration-500 ease-out">
          {pillars.map((pillar, i) => {
            const isActive = activeIdx === i;
            return (
              <div
                key={pillar.num}
                className="group relative rounded-3xl overflow-hidden cursor-pointer transition-all duration-500 ease-out bg-[#090E1A] border border-white/20 shadow-2xl hover:border-[#F58220]/60"
                style={{
                  flex: isActive ? '3.5 1 0%' : '1 1 0%',
                  transition: 'flex 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onMouseEnter={() => setActiveIdx(i)}
              >
                <img
                  src={pillar.image}
                  alt={pillar.title}
                  className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
                  style={{
                    opacity: isActive ? 0.85 : 0.65,
                    filter: 'contrast(1.25) brightness(0.95)',
                    transform: isActive ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.7s ease',
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-72 h-72 bg-[#F58220]/20 rounded-full blur-3xl pointer-events-none" />

                {/* Collapsed Vertical Rail */}
                <div
                  className="absolute inset-0 p-6 flex flex-col justify-between items-center pointer-events-none"
                  style={{
                    opacity: isActive ? 0 : 1,
                    transition: 'opacity 0.3s ease',
                  }}
                >
                  <span className="font-mono text-xs font-bold text-[#F58220] drop-shadow">{pillar.num}</span>
                  <span
                    className="font-mono text-[11px] font-semibold text-slate-200 tracking-[0.25em] drop-shadow-md"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                  >
                    {pillar.collapsed}
                  </span>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#F58220]" />
                </div>

                {/* Expanded Editorial Dossier */}
                <div
                  className="absolute inset-0 h-full p-6 sm:p-8 md:p-12 flex flex-col justify-end text-white z-10"
                  style={{
                    opacity: isActive ? 1 : 0,
                    transform: isActive ? 'translateY(0)' : 'translateY(32px)',
                    transition: 'all 0.5s ease',
                    pointerEvents: isActive ? 'auto' : 'none',
                  }}
                >
                  <div className="max-w-xl mb-6">
                    <span className="text-xs font-mono font-bold text-[#F58220] mb-2.5 block uppercase tracking-wider drop-shadow">
                      {pillar.num} // {pillar.category}
                    </span>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-3.5 tracking-tight leading-tight text-white drop-shadow-md">
                      {pillar.title}
                    </h3>
                    <p className="text-slate-200 text-xs sm:text-sm md:text-base leading-relaxed font-sans drop-shadow-sm">
                      {pillar.desc}
                    </p>
                  </div>

                  <div className="pt-4 sm:pt-5 border-t border-white/20 flex items-center justify-between">
                    <button
                      onClick={() => onOpenBriefing && onOpenBriefing('co-design')}
                      className="inline-flex items-center gap-2 text-xs font-mono font-semibold text-[#F58220] hover:text-white transition"
                    >
                      <span>{pillar.link} →</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: stacked cards */}
        <div className="md:hidden flex flex-col gap-4">
          {pillars.map((pillar) => (
            <div
              key={pillar.num}
              className="relative rounded-3xl overflow-hidden h-[400px] bg-[#090E1A]"
            >
              <img
                src={pillar.image}
                alt={pillar.title}
                className="absolute inset-0 w-full h-full object-cover"
                style={{ opacity: 0.7, filter: 'contrast(1.25) brightness(0.95)' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#090E1A] via-[#090E1A]/80 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                <span className="text-xs font-mono font-bold text-[#F58220] mb-2 block uppercase tracking-wider">
                  {pillar.num} // {pillar.category}
                </span>
                <h3 className="text-2xl font-extrabold text-white mb-3 tracking-tight leading-tight">{pillar.title}</h3>
                <p className="text-xs sm:text-sm text-slate-200 leading-relaxed mb-4">{pillar.desc}</p>
                <div className="pt-4 border-t border-white/20 flex items-center justify-between">
                  <button
                    onClick={() => onOpenBriefing && onOpenBriefing('co-design')}
                    className="text-xs font-mono font-semibold text-[#F58220] hover:text-white transition"
                  >
                    {pillar.link} →
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
